import * as React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MetaAdsConfig } from '../types';
import {
  DEFAULT_MIN_D7_SPEND,
  DEFAULT_TOKEN_BUDGET,
  MAX_TOKEN_BUDGET,
} from '../settingsConfig';

// Sonnet 4.6 output price ($15 / 1M tokens) — used only to show the budget's $ ceiling.
const OUTPUT_COST_PER_TOKEN = 15 / 1_000_000;
const usd = (n: number) => `$${n.toFixed(2)}`;

export default function SettingsDialog({
  open,
  config,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  config: MetaAdsConfig | null;
  saving: boolean;
  onClose: () => void;
  onSave: (patch: { min_d7_spend: number; token_budget: number }) => void;
}) {
  const maxBudget = MAX_TOKEN_BUDGET;
  const [minSpend, setMinSpend] = React.useState('');
  const [budget, setBudget] = React.useState('');

  // Seed the fields each time the dialog opens — from the saved settings, falling back to
  // the defaults for any value not yet stored (the settings store returns {} until saved).
  React.useEffect(() => {
    if (open) {
      setMinSpend(String(config?.min_d7_spend ?? DEFAULT_MIN_D7_SPEND));
      setBudget(String(config?.token_budget ?? DEFAULT_TOKEN_BUDGET));
    }
  }, [open, config]);

  const minSpendNum = Number(minSpend);
  const budgetNum = Number(budget);
  const minSpendValid = Number.isFinite(minSpendNum) && minSpendNum >= 0;
  const budgetValid = Number.isFinite(budgetNum) && budgetNum >= 1;
  const overCap = budgetValid && budgetNum > maxBudget;
  const effectiveBudget = overCap ? maxBudget : budgetNum;
  const budgetUsd = budgetValid ? usd(effectiveBudget * OUTPUT_COST_PER_TOKEN) : '—';
  const canSave = minSpendValid && budgetValid && !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Meta Ads Insights settings</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Changes apply on the next refresh and to the scheduled 4×/day runs.
        </Typography>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Minimum 7-day ad spend"
            type="number"
            value={minSpend}
            onChange={e => setMinSpend(e.target.value)}
            error={minSpend !== '' && !minSpendValid}
            helperText="Only ads that spent at least this over the last 7 days are analyzed and get a written rationale. Lower = more ads and detail, higher cost."
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            fullWidth
          />
          <TextField
            label="Narrative token budget"
            type="number"
            value={budget}
            onChange={e => setBudget(e.target.value)}
            error={budget !== '' && !budgetValid}
            helperText={`Max tokens for the written analysis per run — a ceiling of ≈ ${budgetUsd} of model output. Hard cap ${maxBudget.toLocaleString()} tokens. If a run needs more it is truncated and flagged here with the cost.`}
            InputProps={{
              endAdornment: <InputAdornment position="end">tokens</InputAdornment>,
            }}
            fullWidth
          />
          {overCap && (
            <Alert severity="info">
              Budget will be capped at {maxBudget.toLocaleString()} tokens (the hard
              limit).
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() =>
            onSave({ min_d7_spend: minSpendNum, token_budget: effectiveBudget })
          }
        >
          {saving ? 'Saving…' : 'Save & refresh'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
